/**
 * @file InvoicingProgressModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Invoicing from Pricing & Costing, as it happens (Cam, 3 Nov
 *              2026 — "show a loading screen with a live progress bar and
 *              info so user can see what is happening"). Follows the server's
 *              job (POST /api/pricing-costing/invoice/start →
 *              GET /api/jobs/:id, every second): raising the invoices here,
 *              then sending them to Xero one at a time — "Xero is busy" is
 *              waited out on the server and said here. Ends with what was
 *              raised, what was not and why, and any invoice still waiting to
 *              go to Xero (it goes by itself later). While it runs a deploy is
 *              refused, and the window cannot be closed: the run carries on
 *              on the server whatever the screen does, and reopens here if the
 *              screen is left and come back to.
 *
 * 1.1.0 (2026-11-03, Cam — approved here, not yet in Xero): each invoice
 * raised but not yet in Xero has "Keep held here" (and "Keep all held here"):
 * it stays on statements, goes to Xero once Xero takes it, and moves from the
 * ordinary "not in Xero" alert to the persistent one, with its reason
 * (POST /api/invoices/:id/hold-here).
 */

import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import LoadingBar from './LoadingBar';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { holdHere } from '../utils/heldHere';
import { useAccess, useReadOnly } from '../hooks/useAccess';
import './InvoicingProgressModal.css';
import './HeldHerePanel.css';

const POLL_MS = 1000;

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function Footer({ done, onClose }) {
  useSlideOutButton([done]);
  return (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout btn-slideout-primary" disabled={!done} onClick={onClose}
          title={done ? '' : 'Invoicing is running — it can be closed when it has finished'}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
          <span className="btn-label">{done ? 'Close' : 'Running…'}</span>
        </button>
      </div>
    </div>
  );
}

/**
 * @param {string}   jobId
 * @param {object}   [start]   what the start answered: { total, results } (refusals found before it began)
 * @param {function} onClose   (result|null) — the run finished (or was lost) and the window was closed
 */
/** "Keep held here" on the invoices that did not reach Xero (1.1.0). */
function useHoldNotSent() {
  const access = useAccess();
  const { readOnly, reason } = useReadOnly();
  const [held, setHeld] = useState({}); // invoice id → 'held' | 'working' | error text
  const block = readOnly ? (reason || 'View only') : (!access.can('invoices.create') ? "Holding it needs 'Create invoices'" : null);
  const hold = async (items) => {
    for (const n of items) {
      if (!n?.id || held[n.id] === 'held') continue;
      setHeld(prev => ({ ...prev, [n.id]: 'working' }));
      try {
        await holdHere(n.id);
        setHeld(prev => ({ ...prev, [n.id]: 'held' }));
      } catch (err) {
        setHeld(prev => ({ ...prev, [n.id]: err.message }));
      }
    }
  };
  return { held, hold, block };
}

function InvoicingProgressModal({ jobId, start = null, onClose }) {
  const [job, setJob] = useState(null);
  const [lost, setLost] = useState(null);
  const holding = useHoldNotSent();
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/jobs/${encodeURIComponent(jobId)}`, { credentials: 'include' });
        if (res.status === 404) {
          if (alive) setLost('The invoicing run is no longer on the server — the system was restarted while it ran. Every invoice already raised is kept; refresh the screen and invoice what is left.');
          return;
        }
        const body = await res.json().catch(() => null);
        if (!alive) return;
        if (body) setJob(body);
        if (body && (body.status === 'completed' || body.status === 'failed')) return;
      } catch (_) {
        // A dropped connection: keep asking.
      }
      if (alive) timer.current = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { alive = false; clearTimeout(timer.current); };
  }, [jobId]);

  const p = job?.progress || {};
  const done = !!lost || job?.status === 'completed' || job?.status === 'failed';
  const result = job?.result || null;
  const sending = p.phase === 'sending' || p.phase === 'done';
  const total = sending ? (p.sendTotal || 0) : (p.total || start?.total || 0);
  const current = Math.min(p.current || 0, total);
  const pct = total ? Math.round((current / total) * 100) : (done ? 100 : 0);

  const refused = (result?.results || start?.results || []).filter(r => r.outcome !== 'invoiced');
  const notSent = result?.xero?.notSent || [];

  return (
    <Modal isOpen onClose={() => { if (done) onClose(result); }} title="Invoicing" size="md"
      className="invoicing-progress" footer={<Footer done={done} onClose={() => onClose(result)} />}>
      {lost ? (
        <p className="ip-lost">{lost}</p>
      ) : (
        <>
          <ol className="ip-steps" aria-label="Steps">
            <li className={p.phase === 'raising' ? 'on' : (sending ? 'done' : '')}>
              <span className="material-symbols-rounded">{sending ? 'check_circle' : 'receipt_long'}</span>
              Raising the invoices here
            </li>
            <li className={p.phase === 'sending' ? 'on' : (p.phase === 'done' ? 'done' : '')}>
              <span className="material-symbols-rounded">{p.phase === 'done' ? 'check_circle' : 'cloud_upload'}</span>
              Sending them to Xero
            </li>
          </ol>

          <LoadingBar progress={pct} message={p.message || 'Starting…'} indeterminate={!job} />
          <p className="ip-count">
            {sending ? `${current} of ${plural(total, 'invoice')} sent to Xero` : `${current} of ${plural(total, 'order')} invoiced`}
          </p>

          <dl className="ip-figures">
            <div><dt>Invoices raised</dt><dd>{p.raised || 0}</dd></div>
            <div><dt>Orders not invoiced</dt><dd className={(p.refused || 0) + (p.failed || 0) ? 'warn' : ''}>{(p.refused || 0) + (p.failed || 0)}</dd></div>
            <div><dt>Sent to Xero</dt><dd>{p.sent || 0}</dd></div>
            <div><dt>Waiting to go to Xero</dt><dd className={p.waiting ? 'warn' : ''}>{p.waiting || 0}</dd></div>
          </dl>

          {!done && (
            <p className="ip-note">
              <span className="material-symbols-rounded">info</span>
              Xero takes about 60 requests a minute, so the invoices go one at a time; when Xero says it is busy the run waits
              and tries again. Nothing can be deployed until this has finished. You can leave this screen — it carries on,
              and shows here again when you come back.
            </p>
          )}

          {!done && (p.recent || []).length > 0 && (
            <ul className="ip-recent" aria-label="Latest">
              {p.recent.map((line, i) => <li key={`${i}-${line}`}>{line}</li>)}
            </ul>
          )}

          {done && (
            <div className="ip-result">
              <p className="ip-summary">{p.message || (job?.status === 'failed' ? `Stopped: ${job.error}` : 'Finished')}</p>
              {result?.error && <p className="ip-warn">Stopped part way: {result.error}. Every invoice raised before then is kept.</p>}
              {refused.length > 0 && (
                <>
                  <h4>{plural(refused.length, 'order')} not invoiced</h4>
                  <ul className="ip-list">{refused.map((r, i) => <li key={`r${i}`}>{r.message}</li>)}</ul>
                </>
              )}
              {notSent.length > 0 && (
                <>
                  <h4>{plural(notSent.length, 'invoice')} raised but not yet in Xero</h4>
                  <p className="ip-sub">
                    Each is saved, and goes to Xero by itself at the next send (every 10 minutes in the day, and at 03:00).
                    Where Xero refused one (no stock on its date), keep it held here: it stays on statements, goes once Xero
                    takes it, and an alert stays for everyone until then.
                  </p>
                  {notSent.filter(n => n.id && holding.held[n.id] !== 'held').length > 1 && (
                    <button type="button" data-write-action className={`btn-icon ip-hold${holding.block ? ' btn-disabled' : ''}`}
                      disabled={!!holding.block}
                      title={holding.block || `Keep all ${notSent.length} held here`}
                      aria-label="Keep all held here"
                      onClick={() => holding.hold(notSent)}>
                      <span className="material-symbols-rounded">push_pin</span>
                      <span className="ip-held-note">Keep all held here</span>
                    </button>
                  )}
                  <ul className="ip-list">{notSent.map((n, i) => {
                    const st = n.id ? holding.held[n.id] : null;
                    return (
                      <li key={`n${i}`}><strong>{n.invoice_no}</strong> — {n.reason}
                        {st === 'held' && <span className="ip-held-note">Held here — in the Alerts until it is in Xero</span>}
                        {st && st !== 'held' && st !== 'working' && <span className="ip-held-note">{st}</span>}
                        {n.id && st !== 'held' && (
                          <button type="button" data-write-action
                            className={`btn-icon ip-hold${holding.block || st === 'working' ? ' btn-disabled' : ''}`}
                            disabled={!!holding.block || st === 'working'}
                            title={holding.block || `Keep ${n.invoice_no} held here — approved here only, sent to Xero once fixed`}
                            aria-label={`Keep ${n.invoice_no} held here`}
                            onClick={() => holding.hold([n])}>
                            <span className="material-symbols-rounded">push_pin</span>
                          </button>
                        )}
                      </li>
                    );
                  })}</ul>
                </>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default InvoicingProgressModal;
